
import React from 'react';
import { Control, useWatch } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CalendarDays, MapPin, Link as LinkIcon, Award, Clock, Eye, Tag } from 'lucide-react';
import { EventFormValues } from '../schemas/eventFormSchema';
import { getEventDateRangeString } from '@/lib/eventHelpers';
import { eventTypeLabels } from '@/lib/eventLabels';
import { useQuery } from '@tanstack/react-query';
import { loadProfessions } from '@/services/professionService';
import { format, isToday, isTomorrow } from 'date-fns';
import { hu } from 'date-fns/locale';
import StatusBadge from '@/components/admin/StatusBadge';
import { EventStatus } from '@/services/eventService';

interface EventPreviewProps {
  control: Control<EventFormValues>;
}

const EventPreview = ({ control }: EventPreviewProps) => {
  const eventName = useWatch({ control, name: 'eventName', defaultValue: 'Esemény neve' });
  const description = useWatch({ control, name: 'description', defaultValue: 'Az esemény leírása...' });
  const startDate = useWatch({ control, name: 'startDate', defaultValue: new Date() });
  const endDate = useWatch({ control, name: 'endDate', defaultValue: new Date() });
  const startTime = useWatch({ control, name: 'startTime', defaultValue: new Date() });
  const endTime = useWatch({ control, name: 'endTime', defaultValue: new Date() });
  const timeNotSpecified = useWatch({ control, name: 'timeNotSpecified', defaultValue: false });
  const isOnline = useWatch({ control, name: 'isOnline', defaultValue: false });
  const city = useWatch({ control, name: 'city', defaultValue: '' });
  const venueName = useWatch({ control, name: 'venueName', defaultValue: '' });
  const address = useWatch({ control, name: 'address', defaultValue: '' });
  const eventUrl = useWatch({ control, name: 'eventUrl', defaultValue: '' });
  const eventType = useWatch({ control, name: 'eventType', defaultValue: 'conference' });
  const creditPoints = useWatch({ control, name: 'creditPoints', defaultValue: '' });
  const postAccredited = useWatch({ control, name: 'postAccredited', defaultValue: false });
  const professions = useWatch({ control, name: 'professions', defaultValue: [] });
  const status = useWatch({ control, name: 'status', defaultValue: EventStatus.DRAFT });

  const { data: allProfessions = [] } = useQuery({
    queryKey: ['professions'],
    queryFn: loadProfessions,
  });

  let dateDisplayText = '';

  const isSingleDayEvent = startDate.getDate() === endDate.getDate() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getFullYear() === endDate.getFullYear();

  // Check for today or tomorrow events
  if (isToday(startDate)) {
    if (!timeNotSpecified) {
      dateDisplayText = `Ma, ${format(startTime, 'HH:mm', { locale: hu })} órakor`;
    } else {
      dateDisplayText = 'Ma';
    }
  } else if (isTomorrow(startDate)) {
    if (!timeNotSpecified) {
      dateDisplayText = `Holnap, ${format(startTime, 'HH:mm', { locale: hu })} órakor`;
    } else {
      dateDisplayText = 'Holnap';
    }
  } else if (timeNotSpecified) {
    if (isSingleDayEvent) {
      dateDisplayText = format(startDate, 'yyyy. MMMM d.', { locale: hu });
    } else {
      dateDisplayText = `${format(startDate, 'yyyy. MMMM d.', { locale: hu })} - ${format(endDate, 'yyyy. MMMM d.', { locale: hu })}`;
    }
  } else {
    if (isSingleDayEvent) {
      const startFormatted = format(startTime, 'HH:mm', { locale: hu });
      const endFormatted = format(endTime, 'HH:mm', { locale: hu });
      dateDisplayText = `${format(startDate, 'yyyy. MMMM d.', { locale: hu })}, ${startFormatted} - ${endFormatted}`;
    } else {
      const startDateFormatted = format(startDate, 'yyyy. MMMM d.', { locale: hu });
      const endDateFormatted = format(endDate, 'yyyy. MMMM d.', { locale: hu });
      const startTimeFormatted = format(startTime, 'HH:mm', { locale: hu });
      const endTimeFormatted = format(endTime, 'HH:mm', { locale: hu });
      dateDisplayText = `${startDateFormatted}, ${startTimeFormatted} - ${endDateFormatted}, ${endTimeFormatted}`;
    }
  }

  const selectedProfessionNames = professions
    .map((id: string) => {
      const profession = allProfessions.find(p => p.id === id);
      return profession ? profession.name : id;
    })
    .slice(0, 3);

  return (
    <Card className="h-full border-2 border-medihub-primary/20 sticky top-4 shadow-md overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-medihub-primary/20 to-medihub-accent/10 pb-2">
        <CardTitle className="text-lg flex items-center gap-2 text-medihub-primary">
          <Eye className="h-5 w-5" />
          Esemény előnézet
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[calc(100vh-250px)] p-4">
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-xl font-bold">{eventName || 'Esemény neve'}</h3>
                <StatusBadge status={status} className="ml-2" />
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {eventType && (
                  <Badge variant="outline" className="px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-800">
                    {eventTypeLabels[eventType as keyof typeof eventTypeLabels] || 'Esemény típusa'}
                  </Badge>
                )}

                {(creditPoints || postAccredited) && (
                  <Badge className="px-3 py-1.5 text-sm font-medium bg-blue-100 text-blue-700 flex items-center gap-1.5">
                    <Award className="h-3.5 w-3.5" />
                    <span>
                      {creditPoints
                        ? `${creditPoints} kreditpont`
                        : postAccredited
                          ? "Utóakkreditált"
                          : ""}
                    </span>
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CalendarDays className="h-5 w-5 text-medihub-accent flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Dátum és időpont</h3>
                <p className="text-gray-600">{dateDisplayText}</p>
              </div>
            </div>

            {isOnline ? (
              <div className="flex items-start gap-3">
                <LinkIcon className="h-5 w-5 text-medihub-accent flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900">Online esemény</h3>
                  <p className="text-gray-600">
                    {eventUrl || 'Az esemény URL-je itt fog megjelenni'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-medihub-accent flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900">Helyszín</h3>
                  <p className="text-gray-600">
                    {city && venueName ? `${city}, ${venueName}` : 'A helyszín adatai itt fognak megjelenni'}
                  </p>
                  <p className="text-gray-500">{address}</p>
                </div>
              </div>
            )}

            {selectedProfessionNames.length > 0 && (
              <div className="flex items-start gap-3">
                <Tag className="h-5 w-5 text-medihub-accent flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900">Célközönség szakmák szerint</h3>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedProfessionNames.map((name: string, index: number) => (
                      <Badge key={index} variant="outline" className="bg-gray-100">
                        {name}
                      </Badge>
                    ))}
                    {professions.length > 3 && (
                      <Badge variant="outline" className="bg-gray-100">
                        +{professions.length - 3} további
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(creditPoints || postAccredited) && (
              <div className="flex items-start gap-3">
                <Award className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900">Kreditpont</h3>
                  {creditPoints ? (
                    <p className="text-gray-600">
                      Ez az esemény <span className="font-semibold">{creditPoints} kreditpont</span> megszerzésére ad lehetőséget.
                    </p>
                  ) : postAccredited ? (
                    <p className="text-gray-600">
                      Ez az esemény <span className="font-semibold">Utóakkreditált</span>,  a pontok száma még nem ismert.
                    </p>
                  ) : null}
                </div>
              </div>
            )}

            <div className="border-t pt-4">
              <h3 className="font-medium text-gray-900 mb-2">Leírás</h3>
              <p className="text-gray-600 whitespace-pre-line">{description}</p>
            </div>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default EventPreview;
